import React, { useState, useEffect, useCallback } from "react";
import {
  Building2, Trophy, TrendingDown, TrendingUp, Percent, Info,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Card, Badge, Spinner, PageHeader } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, pct, PeriodPicker, BranchFilter } from "../../components/finance";

function HighlightCard({ icon: Icon, tone, label, branch, metric, testid }) {
  const tones = {
    success: "bg-success-light text-success-text",
    danger: "bg-danger-light text-danger-text",
    info: "bg-blue-100 text-blue-700",
    warn: "bg-warn-light text-warn-text",
  };
  return (
    <Card className="flex items-start gap-3" data-testid={testid}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={18} /></div>
      <div className="min-w-0">
        <p className="text-xs text-ink-mute">{label}</p>
        <p className="truncate font-heading text-lg font-bold text-ink">{branch?.branch_name || "—"}</p>
        <p className="text-xs font-semibold text-ink-soft">{metric}</p>
      </div>
    </Card>
  );
}

export default function OutletAnalyzer() {
  const { t } = useT();
  const toast = useToast();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/outlet-analyzer", { params: { period } }); setData(data); }
    catch { toast.error(t("Failed to load")); }
    finally { setLoading(false); }
  }, [period, t, toast]);
  useEffect(() => { load(); }, [load]);

  const h = data?.highlights || {};

  return (
    <div data-testid="outlet-analyzer-page">
      <PageHeader title={t("Multi-Outlet Analyzer")} subtitle={t("Compare branch performance — revenue, cost & profit")} icon={Building2}>
        <BranchFilter value={branchId} onChange={setBranchId} testid="analyzer-branch-filter" />
        <PeriodPicker value={period} onChange={setPeriod} />
      </PageHeader>

      {loading || !data ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-5">
          {/* Highlights */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <HighlightCard icon={Trophy} tone="success" label={t("Highest Profit Branch")} branch={h.highest_profit_branch} metric={rp(h.highest_profit_branch?.net_profit)} testid="highlight-highest-profit" />
            <HighlightCard icon={TrendingDown} tone="danger" label={t("Lowest Profit Branch")} branch={h.lowest_profit_branch} metric={rp(h.lowest_profit_branch?.net_profit)} testid="highlight-lowest-profit" />
            <HighlightCard icon={TrendingUp} tone="info" label={t("Highest Growth Branch")} branch={h.highest_growth_branch} metric={h.highest_growth_branch?.revenue_growth_pct != null ? `▲ ${pct(h.highest_growth_branch.revenue_growth_pct)}` : t("no data")} testid="highlight-highest-growth" />
            <HighlightCard icon={Percent} tone="warn" label={t("Lowest Margin Branch")} branch={h.lowest_margin_branch} metric={`${t("Margin")} ${pct(h.lowest_margin_branch?.net_margin_pct)}`} testid="highlight-lowest-margin" />
          </div>

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card data-testid="chart-revenue-cogs">
              <h3 className="mb-2 flex items-center gap-2 font-heading font-bold text-ink"><Building2 size={18} /> {t("Revenue by Branch")} & {t("COGS by Branch")}</h3>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.branches} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="branch_name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                    <Tooltip formatter={(v) => rp(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="revenue" radius={[5, 5, 0, 0]} fill="#f97316" name={t("Revenue")} />
                    <Bar dataKey="cogs" radius={[5, 5, 0, 0]} fill="#fbbf24" name={t("COGS")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card data-testid="chart-gross-net">
              <h3 className="mb-2 flex items-center gap-2 font-heading font-bold text-ink"><Trophy size={18} /> {t("Gross Profit by Branch")} & {t("Net Profit by Branch")}</h3>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.branches} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="branch_name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                    <Tooltip formatter={(v) => rp(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="gross_profit" radius={[5, 5, 0, 0]} fill="#3b82f6" name={t("Gross Profit")} />
                    <Bar dataKey="net_profit" radius={[5, 5, 0, 0]} fill="#22c55e" name={t("Net Profit")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Comparison table */}
          <Card data-testid="branch-comparison-table">
            <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Building2 size={18} /> {t("Branch Comparison")}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                  <tr>
                    <th className="py-2 pr-3">{t("Branch")}</th>
                    <th className="py-2 pr-3 text-right">{t("Revenue")}</th>
                    <th className="py-2 pr-3 text-right">{t("COGS")}</th>
                    <th className="py-2 pr-3 text-right">{t("Gross Profit")}</th>
                    <th className="py-2 pr-3 text-right">{t("Overhead")}</th>
                    <th className="py-2 pr-3 text-right">{t("Net Profit")}</th>
                    <th className="py-2 pr-3 text-right">{t("Margin")}</th>
                    <th className="py-2 pr-3 text-right">{t("Growth")}</th>
                    <th className="py-2 pr-3 text-right">{t("Share")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.branches.map((b, i) => (
                    <tr key={i} data-testid={`branch-row-${i}`} className={branchId && b.branch_id === branchId ? "bg-primary-light/60 ring-1 ring-primary/30" : ""}>
                      <td className="py-2.5 pr-3 font-medium text-ink">{b.branch_name}</td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(b.revenue)}</td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(b.cogs)}</td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(b.gross_profit)}</td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(b.overhead)}</td>
                      <td className={`py-2.5 pr-3 text-right font-semibold ${b.net_profit >= 0 ? "text-success-text" : "text-danger-text"}`}>{rp(b.net_profit)}</td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{pct(b.net_margin_pct)}</td>
                      <td className="py-2.5 pr-3 text-right">
                        {b.revenue_growth_pct == null ? <span className="text-ink-mute">—</span> : (
                          <span className={b.revenue_growth_pct >= 0 ? "text-success-text" : "text-danger-text"}>
                            {b.revenue_growth_pct >= 0 ? "▲" : "▼"} {pct(Math.abs(b.revenue_growth_pct))}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-ink-soft">{pct(b.revenue_share_pct)}</td>
                    </tr>
                  ))}
                  <tr className="bg-stone-50 font-semibold" data-testid="branch-row-total">
                    <td className="py-2.5 pr-3 text-ink">{t("Company Total")}</td>
                    <td className="py-2.5 pr-3 text-right text-ink">{rp(data.company.revenue)}</td>
                    <td className="py-2.5 pr-3 text-right text-ink">{rp(data.company.cogs)}</td>
                    <td className="py-2.5 pr-3 text-right text-ink">{rp(data.company.gross_profit)}</td>
                    <td className="py-2.5 pr-3 text-right text-ink">{rp(data.company.overhead)}</td>
                    <td className={`py-2.5 pr-3 text-right ${data.company.net_profit >= 0 ? "text-success-text" : "text-danger-text"}`}>{rp(data.company.net_profit)}</td>
                    <td className="py-2.5 pr-3" colSpan={3} />
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 flex items-start gap-1.5 text-[11px] text-ink-mute">
              <Info size={13} className="mt-0.5 shrink-0" />
              {t("Net profit allocates company-wide overhead by revenue share; branch-tagged expenses are charged directly.")}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}
